import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, Card, Tag, Modal, Spin, message } from 'antd';
import CommonTable from 'components/CommonTable';
import type { ColumnsType } from 'antd/es/table';
import AddAndEditStaff from 'components/AddAndEditStaff';
import type { OpsEditConfig } from 'components/AddAndEditStaff';
import { TTeamMemberDto, TAssignedProvider } from 'types/group';
import { ERoleCode, ERoleDisplayName } from 'types/enumItems';
import {
    updateTeamMemberStatus,
    getTeamMemberList,
    deactivateHVA,
    reactivateHVA,
    IStaffMembership,
} from 'api/operation/group';
import useStaffDetail from './useStaffDetail';
import s from './s.module.less';

const STATUS_DISPLAY: Record<string, { color: string; label: string }> = {
    ACTIVE: { color: 'green', label: 'Active' },
    PENDING: { color: 'gold', label: 'Pending' },
    DISABLED: { color: 'default', label: 'Inactive' },
};

const LOGIN_STATUS_DISPLAY: Record<string, { color: string; label: string }> = {
    NORMAL: { color: 'green', label: 'Active' },
    PROVIDER_LOGIN_NOT: { color: 'default', label: 'Inactive' },
};

const GPStaffDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { loading, data: staffData, refetch: fetchData } = useStaffDetail(id ? Number(id) : undefined);

    const [mainTab, setMainTab] = useState<'staff' | 'gp'>('staff');
    const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });

    const [editModal, setEditModal] = useState<{
        row: IStaffMembership;
        item: TTeamMemberDto;
        opsConfig: OpsEditConfig;
        orgId: number;
    } | null>(null);
    const [selectPracticeModal, setSelectPracticeModal] = useState(false);

    const [confirmModal, setConfirmModal] = useState<{
        action: 'Deactivate' | 'Reactivate';
        row: IStaffMembership;
    } | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [deactivateModal, setDeactivateModal] = useState(false);
    const [overallActionLoading, setOverallActionLoading] = useState(false);

    const isHva = staffData?.isHva === 1;

    useEffect(() => {
        if (staffData) {
            const nameParts = (staffData.name || '').split(' ');
            setEditForm({
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                email: staffData.email || '',
                phone: staffData.phone || '',
            });
        }
    }, [staffData]);

    const openEditModal = useCallback(
        async (row: IStaffMembership) => {
            try {
                const res = await getTeamMemberList(row.orgId, { current: 1, size: 200 });
                const members = res?.data?.data?.records || [];
                const providers = members
                    .filter((m: Record<string, unknown>) => m.roleCode === 'GROUP_PROVIDER')
                    .map((m: Record<string, unknown>) => ({
                        label: `${m.firstName} ${m.lastName}`,
                        value: String(m.providerId),
                    }));

                const nameParts = (staffData?.name || '').split(' ');
                const assignedProviders: TAssignedProvider[] = (row.assignedProviders?.providerIds || []).map((pid) => {
                    const found = members.find((m: Record<string, unknown>) => m.providerId === pid);
                    return {
                        firstName: (found?.firstName as string) || '',
                        lastName: (found?.lastName as string) || '',
                        providerId: pid,
                    };
                });

                const item: TTeamMemberDto = {
                    id: row.memberId,
                    userId: staffData?.userId,
                    firstName: nameParts[0] || '',
                    lastName: nameParts.slice(1).join(' ') || '',
                    email: staffData?.email || '',
                    phoneNumber: staffData?.phone,
                    roleId: row.roleId,
                    roleCode: row.roleName as ERoleCode,
                    isAdmin: row.isAdmin,
                    isAllProviders: row.assignedProviders?.all ?? true,
                    isDefault: false,
                    isOwner: false,
                    portalStatus: '',
                    status: row.status,
                    assignedProviders,
                };

                const opsConfig: OpsEditConfig = {
                    userId: staffData!.userId,
                    memberId: row.memberId,
                    providerOptions: providers,
                };

                setEditModal({ row, item, opsConfig, orgId: row.orgId });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                message.error('Failed to load provider data');
            }
        },
        [staffData],
    );

    const handleMembershipAction = useCallback(async () => {
        if (!confirmModal) return;
        setActionLoading(true);
        try {
            const { action, row } = confirmModal;
            const res = await updateTeamMemberStatus(row.orgId, row.memberId, {
                status: action === 'Deactivate' ? 'DISABLED' : 'ACTIVE',
            });
            if (res && !res.error) {
                message.success(`Role in ${row.gpName} has been ${action.toLowerCase()}d.`);
                setConfirmModal(null);
                fetchData();
            } else {
                message.error(res?.error || `Failed to ${action.toLowerCase()}`);
            }
        } catch {
            // error handled by message.error above
        } finally {
            setActionLoading(false);
        }
    }, [confirmModal, fetchData]);

    const handleOverallAction = useCallback(async () => {
        if (!staffData) return;
        setOverallActionLoading(true);
        try {
            const isActive = isHva ? staffData.loginStatus !== 'PROVIDER_LOGIN_NOT' : staffData.status === 'ACTIVE';
            let res;
            if (isHva) {
                res = isActive ? await deactivateHVA(staffData.userId) : await reactivateHVA(staffData.userId);
            } else {
                res = await updateTeamMemberStatus(
                    staffData.memberships[0]?.orgId,
                    staffData.memberships[0]?.memberId,
                    {
                        status: isActive ? 'DISABLED' : 'ACTIVE',
                    },
                );
            }
            if (res && !res.error) {
                message.success(`${staffData.name} has been ${isActive ? 'deactivated' : 'reactivated'}.`);
                setDeactivateModal(false);
                fetchData();
            } else {
                message.error(res?.error || 'Action failed');
            }
        } catch {
            // error handled by message
        } finally {
            setOverallActionLoading(false);
        }
    }, [staffData, isHva, fetchData]);

    if (loading) {
        return (
            <div className={s.empty}>
                <Spin size="large" />
            </div>
        );
    }

    if (!staffData) {
        return <div className={s.empty}>Member not found</div>;
    }

    const isOverallActive = isHva ? staffData.loginStatus !== 'PROVIDER_LOGIN_NOT' : staffData.status === 'ACTIVE';

    const memberships = staffData.memberships || [];
    const statusInfo = isHva
        ? LOGIN_STATUS_DISPLAY[staffData.loginStatus || ''] || LOGIN_STATUS_DISPLAY.PROVIDER_LOGIN_NOT
        : STATUS_DISPLAY[staffData.status] || STATUS_DISPLAY.DISABLED;
    const isSinglePractice = memberships.length === 1;

    let signpost: string;
    if (isHva) {
        signpost = `${staffData.practicesCount} ${staffData.practicesCount === 1 ? 'practice' : 'practices'} · HVA`;
    } else if (isSinglePractice) {
        signpost = `${memberships[0]?.gpName || ''} · Staff`;
    } else {
        signpost = `${memberships.length} practices · Staff`;
    }

    const columns: ColumnsType<IStaffMembership> = [
        {
            title: 'Practice',
            dataIndex: 'gpName',
            key: 'gpName',
            render: (val: string, record: IStaffMembership) => (
                <a
                    style={{ color: 'var(--primary-color)', fontWeight: 500, cursor: 'pointer' }}
                    onClick={() => {
                        if (record.orgId) {
                            window.open(`/operation/group-practice-detail/${record.orgId}`, '_blank');
                        }
                    }}
                >
                    {val}
                </a>
            ),
        },
        {
            title: 'Owner',
            key: 'ownerProviderName',
            render: (_: unknown, record: IStaffMembership) => (
                record.ownerProviderId ? (
                    <a
                        style={{ color: 'var(--primary-color)', fontWeight: 500, cursor: 'pointer' }}
                        onClick={() => {
                            window.open(`/operation/uniprofile-detail/-1/${record.ownerProviderId}`, '_blank');
                        }}
                    >
                        {record.ownerProviderName || '-'}
                    </a>
                ) : (
                    <span>{record.ownerProviderName || '-'}</span>
                )
            ),
        },
        {
            title: 'Role',
            key: 'role',
            render: (_: unknown, record: IStaffMembership) => (
                <span>
                    {ERoleDisplayName[record.roleName as keyof typeof ERoleDisplayName] || record.roleName}
                    {record.isAdmin && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                            Admin
                        </Tag>
                    )}
                </span>
            ),
        },
        {
            title: 'Invited by',
            key: 'invitedByName',
            render: (_: unknown, record: IStaffMembership) => {
                return <span>{record.invitedByName || '-'}</span>;
            },
        },
        {
            title: 'Joined',
            dataIndex: 'joinedDate',
            key: 'joinedDate',
            render: (val: string | null) => (val ? dayjs(val).format('MM/DD/YYYY') : '-'),
        },
        {
            title: 'Last active',
            dataIndex: 'lastActiveAt',
            key: 'lastActiveAt',
            render: (val: string | null) => (val ? dayjs(val).format('MM/DD') : '—'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (val: string) => {
                const info = STATUS_DISPLAY[val] || STATUS_DISPLAY.DISABLED;
                return <Tag color={info.color}>{info.label}</Tag>;
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: unknown, record: IStaffMembership) => (
                <div style={{ display: 'flex', gap: 16 }}>
                    <Button
                        type="link"
                        style={{ padding: 0, color: 'var(--primary-color)' }}
                        onClick={() => openEditModal(record)}
                    >
                        Edit
                    </Button>
                    {record.status === 'ACTIVE' && (
                        <Button
                            type="link"
                            danger
                            style={{ padding: 0 }}
                            onClick={() => setConfirmModal({ action: 'Deactivate', row: record })}
                        >
                            Deactivate
                        </Button>
                    )}
                    {record.status === 'DISABLED' && (
                        <Button
                            type="link"
                            style={{ padding: 0, color: '#16a34a' }}
                            onClick={() => setConfirmModal({ action: 'Reactivate', row: record })}
                        >
                            Reactivate
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <div className={s.backRow}>
                    <span
                        className={s.backLink}
                        onClick={() => navigate(`/operation/hello-klarity-providers?type=gp&subTab=${isHva ? 'hva' : 'staff'}`)}
                    >
                        ← Back to {isHva ? 'HVA' : 'staff'} list
                    </span>
                </div>
                <div className={s.headerTop}>
                    <h1 className={s.staffName}>{staffData.name}</h1>
                    <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                    {!isHva &&
                        isSinglePractice &&
                        memberships[0] &&
                        memberships[0].roleName !== 'GROUP_PROVIDER' &&
                        memberships[0].roleName !== 'GROUP_OWNER' && (
                            <>
                                <Tag>
                                    {ERoleDisplayName[memberships[0].roleName as keyof typeof ERoleDisplayName] ||
                                        memberships[0].roleName}
                                </Tag>
                                {memberships[0].isAdmin && <Tag color="blue">Admin</Tag>}
                            </>
                        )}
                </div>
                <div className={s.signpost}>{signpost}</div>
            </div>

            <div className={s.tabNav}>
                <span
                    className={`${s.tabItem} ${mainTab === 'staff' ? s.tabItemActive : ''}`}
                    onClick={() => setMainTab('staff')}
                >
                    {isHva ? 'HVA information' : 'Staff information'}
                </span>
                <span
                    className={`${s.tabItem} ${mainTab === 'gp' ? s.tabItemActive : ''}`}
                    onClick={() => setMainTab('gp')}
                >
                    Group practice
                </span>
            </div>

            <div className={s.tabContent}>
                {mainTab === 'staff' && (
                    <>
                        <div className={s.infoSubTabs}>
                            <span className={`${s.infoSubTab} ${s.infoSubTabActive}`}>Basic info</span>
                        </div>

                        <Card className={s.basicInfoCard}>
                            <div className={s.basicInfoHeader}>
                                <span className={s.basicInfoTitle}>Basic information</span>
                            </div>
                            <div className={s.formGrid}>
                                <div className={s.formField}>
                                    <span className={s.formLabel}>First name</span>
                                    <span className={s.formValue}>{editForm.firstName}</span>
                                </div>
                                <div className={s.formField}>
                                    <span className={s.formLabel}>Last name</span>
                                    <span className={s.formValue}>{editForm.lastName}</span>
                                </div>
                                <div className={s.formField}>
                                    <span className={s.formLabel}>Email</span>
                                    <span className={s.formValue}>{editForm.email}</span>
                                </div>
                                <div className={s.formField}>
                                    <span className={s.formLabel}>Phone number</span>
                                    <span className={s.formValue}>{editForm.phone || '-'}</span>
                                </div>
                            </div>
                        </Card>
                    </>
                )}

                {mainTab === 'gp' && (
                    <>
                        <div className={s.practiceCount}>
                            {memberships.length} {memberships.length === 1 ? 'practice' : 'practices'}
                        </div>
                        <CommonTable<IStaffMembership>
                            data={memberships}
                            columns={columns}
                            rowKey="memberId"
                            pagination={false}
                        />
                    </>
                )}
            </div>

            <div className={s.stickyBar}>
                {isHva ? (
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
                        {staffData.loginStatus !== 'PROVIDER_LOGIN_NOT' && (
                            <Button
                                type="link"
                                onClick={() => setDeactivateModal(true)}
                                style={{ color: 'var(--primary-color)' }}
                            >
                                Deactivate (Preferred)
                            </Button>
                        )}
                        {staffData.loginStatus === 'PROVIDER_LOGIN_NOT' && (
                            <Button
                                type="link"
                                onClick={() => setDeactivateModal(true)}
                                style={{ color: 'var(--primary-color)' }}
                            >
                                Reactivate
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <Button
                            type="default"
                            onClick={() => navigate(`/operation/hello-klarity-providers?type=gp&subTab=${isHva ? 'hva' : 'staff'}`)}
                        >
                            Save
                        </Button>
                        <div>
                            {staffData.status === 'ACTIVE' && (
                                <Button
                                    type="link"
                                    onClick={() => setDeactivateModal(true)}
                                    style={{ color: 'var(--primary-color)' }}
                                >
                                    Deactivate (Preferred)
                                </Button>
                            )}
                            {staffData.status === 'DISABLED' && (
                                <Button
                                    type="link"
                                    onClick={() => setDeactivateModal(true)}
                                    style={{ color: 'var(--primary-color)' }}
                                >
                                    Reactivate
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {editModal && (
                <AddAndEditStaff
                    show
                    isOps
                    organizationId={editModal.orgId}
                    item={editModal.item}
                    opsConfig={editModal.opsConfig}
                    onCannel={() => setEditModal(null)}
                    onSuccess={() => {
                        setEditModal(null);
                        fetchData();
                    }}
                />
            )}

            <Modal
                title={confirmModal ? `${confirmModal.action} role in ${confirmModal.row.gpName}?` : ''}
                open={!!confirmModal}
                onCancel={() => setConfirmModal(null)}
                onOk={handleMembershipAction}
                confirmLoading={actionLoading}
                okText={confirmModal?.action}
                okButtonProps={{
                    danger: confirmModal?.action === 'Deactivate',
                    style:
                        confirmModal?.action === 'Reactivate'
                            ? { background: '#16a34a', borderColor: '#16a34a' }
                            : undefined,
                }}
            >
                {confirmModal?.action === 'Deactivate' ? (
                    <p>
                        <strong>{staffData.name}</strong> will no longer have{' '}
                        {ERoleDisplayName[confirmModal.row.roleName as keyof typeof ERoleDisplayName] ||
                            confirmModal.row.roleName}{' '}
                        access to <strong>{confirmModal.row.gpName}</strong>. They will continue to manage their other
                        assigned practices.
                    </p>
                ) : (
                    <p>
                        Reactivate <strong>{staffData.name}</strong>&apos;s{' '}
                        {ERoleDisplayName[confirmModal?.row.roleName as keyof typeof ERoleDisplayName] ||
                            confirmModal?.row.roleName}{' '}
                        access to <strong>{confirmModal?.row.gpName}</strong>?
                    </p>
                )}
            </Modal>

            <Modal
                title={isOverallActive ? `Deactivate ${staffData.name}?` : `Reactivate ${staffData.name}?`}
                open={deactivateModal}
                onCancel={() => setDeactivateModal(false)}
                onOk={handleOverallAction}
                confirmLoading={overallActionLoading}
                okText={isOverallActive ? 'Deactivate' : 'Reactivate'}
                okButtonProps={{
                    danger: isOverallActive,
                    style: !isOverallActive ? { background: '#16a34a', borderColor: '#16a34a' } : undefined,
                }}
            >
                {isOverallActive ? (
                    <p>
                        This will remove <strong>{staffData.name}</strong>&apos;s access to all{' '}
                        {staffData.practicesCount} assigned practices.
                        {isHva ? ' They will no longer be able to manage any group practices on Klarity.' : ''}
                    </p>
                ) : (
                    <p>
                        Reactivate <strong>{staffData.name}</strong>? They will regain access to their
                        {isHva ? ' assigned' : ''} practices.
                    </p>
                )}
            </Modal>

            <Modal
                title="Select practice to edit"
                open={selectPracticeModal}
                onCancel={() => setSelectPracticeModal(false)}
                footer={null}
                destroyOnClose
            >
                <p style={{ marginBottom: 16, color: '#6B7280' }}>
                    This staff member belongs to multiple practices. Select which practice to edit.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {memberships.map((m) => (
                        <Button
                            key={m.memberId}
                            block
                            style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }}
                            onClick={() => {
                                setSelectPracticeModal(false);
                                openEditModal(m);
                            }}
                        >
                            <div style={{ fontWeight: 500 }}>{m.gpName}</div>
                            <div style={{ fontSize: 12, color: '#6B7280' }}>
                                {ERoleDisplayName[m.roleName as keyof typeof ERoleDisplayName] || m.roleName}
                                {m.isAdmin ? ' · Admin' : ''}
                            </div>
                        </Button>
                    ))}
                </div>
            </Modal>
        </div>
    );
};

export default GPStaffDetailPage;
